import { mergeProductionDocuments, validProductionDocument, NOTES_ID_RE, type ProductionDocument } from "./model";
import { TOKEN_RE, ROOM_RE, MAX_MESSAGE_BYTES } from "../../server/protocol.mjs";

export interface NotesCredentials { roomId: string; notesToken: string; projectId: string; relayUrl?: string }
export interface NotesTransportStatus { connected: boolean; error: string | null; peerCount: number }
export interface NotesTransport { publish(document: ProductionDocument): void; close(): void }
const encode = (data: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(data)));
const decode = (value: string) => Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
const encoder = new TextEncoder();
export function validNotesCredentials(value: unknown): value is NotesCredentials {
  if (!value || typeof value !== "object") return false;
  const c = value as NotesCredentials;
  if (typeof c.roomId !== "string" || !ROOM_RE.test(c.roomId) || typeof c.notesToken !== "string" ||
    !TOKEN_RE.test(c.notesToken) || typeof c.projectId !== "string" || !NOTES_ID_RE.test(c.projectId)) return false;
  if (c.relayUrl !== undefined) { try { const u = new URL(c.relayUrl); if (!["http:", "https:"].includes(u.protocol) || u.username || u.password || u.origin !== c.relayUrl) return false; } catch { return false; } }
  return true;
}
/** Existing operator rooms gain notes access without changing their live display invitations. */
export async function ensureNotesCredentials(credentials: { roomId: string; controllerToken?: string; relayUrl?: string }, projectId: string): Promise<NotesCredentials> {
  if (!ROOM_RE.test(credentials.roomId) || !NOTES_ID_RE.test(projectId) || !credentials.controllerToken)
    throw new Error("An operator room is required to share production notes.");
  const key = `cue-studio-notes-credentials-v1:${credentials.roomId}:${projectId}`;
  let cached: NotesCredentials | null = null;
  try { const value: unknown = JSON.parse(localStorage.getItem(key) || "null"); if (validNotesCredentials(value) && value.projectId === projectId && value.roomId === credentials.roomId && value.relayUrl === credentials.relayUrl) cached = value; } catch { /* Reconnect can repair missing cache. */ }
  if (credentials.relayUrl) {
    let serverRejected = false;
    try {
      const response = await fetch(`${credentials.relayUrl}/api/notes/credentials`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${credentials.controllerToken}`, "X-Cue-Room": credentials.roomId },
        body: JSON.stringify({ projectId }), signal: AbortSignal.timeout(5000),
      });
      if (!response.ok) { serverRejected = true; throw new Error(response.status === 401 ? "This live room has expired. Reconnect devices to create a new sharing link." : "Could not prepare production-note sharing."); }
      const result = { ...(await response.json()), relayUrl: credentials.relayUrl };
      if (!validNotesCredentials(result) || result.projectId !== projectId || result.roomId !== credentials.roomId) throw new Error("Invalid notes credentials.");
      try { localStorage.setItem(key, JSON.stringify(result)); } catch { /* Document saving reports its own storage failures. */ }
      return result;
    } catch (error) {
      if (cached && !serverRejected) return cached;
      throw error;
    }
  }
  if (cached) return cached;
  if (!crypto.subtle) throw new Error("A secure browser context is required for separate-tab sharing.");
  const signingKey = await crypto.subtle.importKey("raw", encoder.encode(credentials.controllerToken), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  const notesToken = encode(await crypto.subtle.sign("HMAC", signingKey, encoder.encode(`cue-production-notes-v1:${credentials.roomId}:${projectId}`))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const result = { roomId: credentials.roomId, projectId, notesToken };
  try { localStorage.setItem(key, JSON.stringify(result)); } catch { /* Deterministic capability remains recoverable. */ }
  return result;
}

/** Isolated notes connection. It never authenticates to, receives from or writes to prompt transport. */
export function createNotesTransport(credentials: NotesCredentials, callbacks: {
  onDocument(document: ProductionDocument): void; onStatus(status: NotesTransportStatus): void;
}): NotesTransport {
  if (!validNotesCredentials(credentials)) throw new Error("Invalid production-note link.");
  let closed = false, current: ProductionDocument | null = null, socket: WebSocket | null = null;
  let ready = false, relayPeers = 0, reconnectAttempt = 0, error: string | null = null, lastMessage = Date.now();
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined, publishTimer: ReturnType<typeof setTimeout> | undefined;
  let channel: BroadcastChannel | null = null, sequence = 0;
  const sender = crypto.randomUUID?.() || [...crypto.getRandomValues(new Uint8Array(16))].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  const seen = new Map<string, number>(), localPeers = new Map<string, number>();
  const channelKey = crypto.subtle ? crypto.subtle.digest("SHA-256", encoder.encode(credentials.notesToken))
    .then((raw) => crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"])) : null;
  const status = () => { if (!closed) callbacks.onStatus({ connected: ready || localPeers.size > 0, error, peerCount: Math.max(Math.max(0, relayPeers - 1), localPeers.size) }); };
  const send = (message: unknown) => {
    if (socket?.readyState !== WebSocket.OPEN || socket.bufferedAmount > MAX_MESSAGE_BYTES * 2) return false;
    try { socket.send(JSON.stringify(message)); return true; } catch { return false; }
  };
  async function localSend(kind: "hello" | "state") {
    if (!channel || !channelKey || closed || (kind === "state" && !current)) return;
    const payload = JSON.stringify({ version: 1, sender, sequence: ++sequence, kind, ...(kind === "state" ? { document: current } : {}) });
    try {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ciphertext = encode(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await channelKey, encoder.encode(payload)));
      if (!closed) channel?.postMessage({ ciphertext, iv: encode(iv.buffer) });
    } catch { /* Relay remains available if local browser messaging is denied. */ }
  }
  function accept(document: unknown) {
    if (!validProductionDocument(document) || document.projectId !== credentials.projectId) return;
    try {
      const merged = current ? mergeProductionDocuments(current, document) : document;
      if (JSON.stringify(merged) === JSON.stringify(current)) return;
      current = merged; callbacks.onDocument(merged);
    } catch (e) { error = e instanceof Error ? e.message : "Production notes could not merge."; status(); }
  }
  function flush() {
    clearTimeout(publishTimer); publishTimer = undefined;
    if (closed || !current) return;
    if (ready) send({ type: "notes-publish", document: current });
    void localSend("state");
  }
  if (channelKey && typeof BroadcastChannel !== "undefined") {
    try {
      channel = new BroadcastChannel(`cue-production-notes:${credentials.roomId}:${credentials.projectId}`);
      channel.onmessage = async ({ data }) => {
        try {
          if (closed || !data || typeof data.ciphertext !== "string" || data.ciphertext.length > MAX_MESSAGE_BYTES || typeof data.iv !== "string" || data.iv.length !== 16) return;
          const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: decode(data.iv) }, await channelKey, decode(data.ciphertext));
          if (closed) return;
          const message = JSON.parse(new TextDecoder().decode(plaintext));
          if (message.version !== 1 || typeof message.sender !== "string" || !NOTES_ID_RE.test(message.sender) || message.sender === sender || !Number.isSafeInteger(message.sequence) || message.sequence <= (seen.get(message.sender) || 0)) return;
          if (seen.size >= 128 && !seen.has(message.sender)) seen.delete(seen.keys().next().value!);
          seen.set(message.sender, message.sequence);
          if (localPeers.size >= 128 && !localPeers.has(message.sender)) localPeers.delete(localPeers.keys().next().value!);
          localPeers.set(message.sender, Date.now()); status();
          if (message.kind === "hello") void localSend("state");
          else if (message.kind === "state") { accept(message.document); if (ready && current) send({ type: "notes-publish", document: current }); }
        } catch { /* Unauthenticated, malformed and cross-project local messages have no effect. */ }
      };
      void localSend("hello");
    } catch { channel = null; }
  }
  function connect() {
    if (closed || !credentials.relayUrl || socket && [WebSocket.OPEN, WebSocket.CONNECTING].includes(socket.readyState as 0 | 1)) return;
    clearTimeout(reconnectTimer);
    const url = new URL("/ws", credentials.relayUrl); url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(url); socket = ws; lastMessage = Date.now();
    ws.onopen = () => { if (socket === ws && !closed) send({ type: "notes-auth", roomId: credentials.roomId, projectId: credentials.projectId, notesToken: credentials.notesToken }); };
    ws.onmessage = ({ data }) => {
      if (closed || socket !== ws || typeof data !== "string" || encoder.encode(data).length > MAX_MESSAGE_BYTES) return;
      try {
        const message = JSON.parse(data); lastMessage = Date.now();
        if (message.type === "notes-ready" && message.projectId === credentials.projectId) { ready = true; reconnectAttempt = 0; error = null; status(); flush(); }
        else if (message.type === "notes-state") accept(message.document);
        else if (message.type === "notes-peers" && Number.isInteger(message.count)) { relayPeers = Math.max(0, Math.min(32, message.count)); status(); }
      } catch { /* Invalid traffic cannot change cached notes. */ }
    };
    ws.onerror = () => { /* Close schedules a retry; cached notes remain editable. */ };
    ws.onclose = ({ code }) => {
      if (socket !== ws || closed) return;
      socket = null; ready = false; relayPeers = 0;
      if (code === 1008) error = "This sharing link is no longer accepted. Keep your saved notes and ask the operator for a new link.";
      else if (code === 1013) error = "The live notes service is full. Notes remain on this device; export a backup.";
      status();
      if (code !== 1008) reconnectTimer = setTimeout(connect, Math.min(15000, 800 * 2 ** Math.min(5, reconnectAttempt++)));
    };
  }
  const resume = () => { if (closed) return; if (socket && Date.now() - lastMessage > 40000) socket.close(); else { connect(); if (ready) { send({ type: "notes-sync" }); flush(); } } void localSend("hello"); };
  const pulse = setInterval(() => {
    for (const [id, at] of localPeers) if (Date.now() - at > 45000) localPeers.delete(id);
    status();
    if (socket && Date.now() - lastMessage > 40000) socket.close();
    else if (ready) { send({ type: "notes-ping" }); send({ type: "notes-sync" }); }
    void localSend("hello");
  }, 15000);
  window.addEventListener("online", resume); window.addEventListener("pageshow", resume);
  const visibility = () => { if (document.visibilityState === "visible") resume(); };
  document.addEventListener("visibilitychange", visibility);
  connect(); status();
  return {
    publish(document) {
      if (closed) return;
      if (!validProductionDocument(document) || document.projectId !== credentials.projectId) throw new Error("Production notes belong to another project.");
      current = current ? mergeProductionDocuments(current, document) : document;
      clearTimeout(publishTimer); publishTimer = setTimeout(flush, 120);
    },
    close() {
      closed = true; clearInterval(pulse); clearTimeout(reconnectTimer); clearTimeout(publishTimer);
      socket?.close(); channel?.close(); window.removeEventListener("online", resume); window.removeEventListener("pageshow", resume); document.removeEventListener("visibilitychange", visibility);
    },
  };
}
